import React, { useState, useEffect } from 'react';
import { isBluetoothSupported, BLUETOOTH_STATUS, isStandaloneMode, getDistinctLocations, getDevicesByLocation } from './config/devices';
import bluetoothService from './services/bluetoothService';
import JourneyApp from './components/JourneyApp';
import { PageLayout } from './components/PageLayout';

function App() {
  // App state management
  const [currentScreen, setCurrentScreen] = useState('connection'); // 'connection' or 'journey'
  const [selectedDeviceId, setSelectedDeviceId] = useState('standalone'); // Default to standalone
  const [bluetoothStatus, setBluetoothStatus] = useState(BLUETOOTH_STATUS.DISCONNECTED);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(true); // Default to standalone
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [favouriteClickData, setFavouriteClickData] = useState(null);
  const [favouritesRefreshTrigger, setFavouritesRefreshTrigger] = useState(0);

  // Load saved location from localStorage on mount
  useEffect(() => {
    const savedLocation = localStorage.getItem('journey-alerts-location');
    if (savedLocation) {
      setSelectedLocation(savedLocation);
      setShowLocationPicker(false);
    } else {
      setShowLocationPicker(true); // Show location picker if no saved location
    }
  }, []);

  useEffect(() => {
    // Set up bluetooth status listener
    bluetoothService.onStatusChange = (status) => {
      setBluetoothStatus(status);
      if (status === BLUETOOTH_STATUS.ERROR) {
        setIsConnecting(false);
      } else if (status === BLUETOOTH_STATUS.CONNECTED) {
        // Switch to journey screen after successful connection
        setTimeout(() => {
          setCurrentScreen('journey');
        }, 1000); // Brief delay to show success status
      } else if (status === BLUETOOTH_STATUS.DISCONNECTED) {
        // Ensure connecting state is reset when disconnected
        setIsConnecting(false);
      }
    };

    // Set up connection loss handler
    bluetoothService.onConnectionLost = () => {
      console.log('Connection lost - returning to device selection screen');
      setCurrentScreen('connection');
      setError('Connection lost - please reconnect');
      // Reset connection state to ensure proper button display
      setIsConnecting(false);
      setBluetoothStatus(BLUETOOTH_STATUS.DISCONNECTED);
    };

    return () => {
      bluetoothService.onStatusChange = null;
      bluetoothService.onConnectionLost = null;
    };
  }, []);

  // Reset connection state whenever we switch to connection screen
  useEffect(() => {
    if (currentScreen === 'connection') {
      // Ensure clean state when returning to connection screen
      setIsConnecting(false);
      if (bluetoothStatus !== BLUETOOTH_STATUS.DISCONNECTED) {
        setBluetoothStatus(BLUETOOTH_STATUS.DISCONNECTED);
      }
    }
  }, [currentScreen, bluetoothStatus]);

  // Listen for fullscreen changes (when user exits fullscreen with Escape key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement ||
        document.mozFullScreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
    setShowLocationPicker(false);
    setSelectedDeviceId('standalone'); // Reset to standalone when location changes
    setIsStandalone(true);
    setError(null);
    
    // Save to localStorage
    localStorage.setItem('journey-alerts-location', location);
  };

  const handleChangeLocation = () => {
    setShowLocationPicker(true);
  };

  const handleDeviceChange = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setIsStandalone(isStandaloneMode(deviceId));
    setError(null); // Clear any previous errors when device changes
  };

  const handleConnect = async () => {
    // Check if standalone mode is selected
    if (isStandaloneMode(selectedDeviceId)) {
      // For standalone mode, skip Bluetooth and go directly to journey screen
      setCurrentScreen('journey');
      return;
    }

    if (!isBluetoothSupported()) {
      setError('Web Bluetooth is not supported in this browser');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      await bluetoothService.connect(selectedDeviceId);
      // Connection successful - status will be updated via onStatusChange
    } catch (error) {
      setError(error.message);
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await bluetoothService.disconnect();
      setCurrentScreen('connection');
      // Reset connection state to ensure proper button display
      setIsConnecting(false);
      setBluetoothStatus(BLUETOOTH_STATUS.DISCONNECTED);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Error disconnecting:', error);
      setCurrentScreen('connection');
      // Reset connection state even on error
      setIsConnecting(false);
      setBluetoothStatus(BLUETOOTH_STATUS.DISCONNECTED);
      setError(null);
    }
  };

  // Handle fullscreen toggle
  const handleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        // Enter fullscreen mode
        const element = document.documentElement || document.body;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen(); // Safari
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen(); // IE/Edge
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen(); // Firefox
        }
        setIsFullscreen(true);
      } else {
        // Exit fullscreen mode
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        }
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      setError('Fullscreen not supported or blocked by browser');
    }
  };


  // Connection Screen Component
  const ConnectionScreen = () => (
    <PageLayout 
      onFullscreen={handleFullscreen} 
      isFullscreen={isFullscreen}
      selectedLocation={selectedLocation}
      favouritesRefreshTrigger={favouritesRefreshTrigger}
      onFavouriteClick={(destination) => {
        // When favourite clicked from connection screen, automatically connect in standalone mode first
        setFavouriteClickData(destination);
        if (isStandaloneMode(selectedDeviceId)) {
          setCurrentScreen('journey');
        } else {
          // If not in standalone mode, connect first then navigate
          handleConnect().then(() => {
            setCurrentScreen('journey');
          }).catch(() => {
            // If connection fails, clear the favourite data
            setFavouriteClickData(null);
          });
        }
      }}
      isConnectionScreen={true}
    >
      <div className="flex items-center justify-center h-full p-4">
        {/* Central white card content */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 max-w-lg w-full">
          {/* Header with logo */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="relative">
              <img src="/ja_logo.png" alt="Journey Alerts" className="h-14 sm:h-16 lg:h-20" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-0.5 sm:mb-1">
                Journey Alerts
              </h1>
              <p className="text-sm sm:text-base text-gray-600">Ticket Window Interface</p>
            </div>
          </div>
          
          {/* Location Header and Picker */}
          {showLocationPicker ? (
            <div className="mb-6">
              <h2 className="text-lg sm:text-xl lg:text-2xl text-center font-bold mb-4 sm:mb-6">
                Select Location
              </h2>
              <div className="space-y-3">
                {getDistinctLocations().map((location) => (
                  <button
                    key={location}
                    onClick={() => handleLocationChange(location)}
                    className="w-full p-4 text-left bg-gray-50 hover:bg-journeyAlertsPurple hover:text-white rounded-lg transition-colors font-medium"
                  >
                    {location}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold">
                  {selectedLocation}
                </h2>
                <button
                  onClick={handleChangeLocation}
                  className="text-sm text-journeyAlertsPurple hover:text-purple-700 underline"
                >
                  Change Location
                </button>
              </div>

              {/* Device Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Device:
                </label>
                <select
                  value={selectedDeviceId}
                  onChange={(e) => handleDeviceChange(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-journeyAlertsPurple focus:border-transparent"
                  disabled={isConnecting}
                >
                  <option value="standalone">Standalone Mode</option>
                  {selectedLocation && getDevicesByLocation(selectedLocation).map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.bluetoothName}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Connect Button - Only show when location is selected */}
          {!showLocationPicker && (
            <button
              onClick={handleConnect}
              disabled={isConnecting || bluetoothStatus === BLUETOOTH_STATUS.CONNECTED}
              className="w-full bg-journeyAlertsPurple text-white py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition-colors text-lg"
            >
              {isConnecting ? 'Connecting...' : 'Connect'}
            </button>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );

  return (
    <div className={`w-full h-screen max-w-[1280px] mx-auto relative ${isFullscreen ? 'landscape:max-h-[600px]' : 'landscape:max-h-[720px]'}`} style={{height: '100dvh'}}>
      {currentScreen === 'connection' ? (
        <ConnectionScreen />
      ) : (
        <PageLayout 
          onFullscreen={handleFullscreen} 
          isFullscreen={isFullscreen}
          selectedLocation={selectedLocation}
          favouritesRefreshTrigger={favouritesRefreshTrigger}
          onFavouriteClick={(destination) => {
            setFavouriteClickData(destination);
          }}
        >
          <JourneyApp 
            onDisconnect={handleDisconnect} 
            onFullscreen={handleFullscreen} 
            isFullscreen={isFullscreen}
            isStandalone={isStandalone}
            selectedLocation={selectedLocation}
            favouriteClickData={favouriteClickData}
            onFavouriteProcessed={() => setFavouriteClickData(null)}
            onFavouriteUpdated={() => setFavouritesRefreshTrigger(prev => prev + 1)}
          />
        </PageLayout>
      )}
    </div>
  );
}

export default App; 